import React, { useMemo } from 'react';
import {
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  Tooltip,
} from 'recharts';

import { type ChartConfig, ChartContainer } from '@/components/ui/chart';
import type { PerformanceRadar, RadarPoint } from '@/pages/analytics/types';
import {
  COLOR_MUTED,
  COLOR_PRIMARY,
  colorForSeriesIndex,
} from '@/pages/analytics/utils/chartPalette';
import {
  deriveRadarAxesFromPoints,
  hasRadarCurrentData,
  type RadarAxis,
} from '@/pages/analytics/utils/radarAxes';

import { ChartEmptyState } from './ChartStates';
import { DotsLegend } from './DotsLegend';
import type { ChartScope } from './ScopeToggle';

interface PerformanceRadarChartProps {
  data?: PerformanceRadar;
  scope: ChartScope;
  height?: number;
  /**
   * Label for the dashed comparison polygon. Two callers, two semantics:
   *
   * - Org tab: "Prior Period" — `prior_period` represents the same-length
   *   window immediately before `start`, per the API contract.
   * - Agent detail: "Organization Average" — the backend overloads
   *   `prior_period` to carry the org-wide average for this period instead.
   *
   * The frontend just renders what's in `prior_period`; the backend owns the
   * semantic. See specs/AD-XXX-analytics-feature/README.md §6.5 and §13 Q1.
   */
  priorLabel?: string;
}

interface RadarRow {
  axis: string;
  [seriesKey: string]: string | number;
}

// Backend ships axis values in the `categories` map. The duplicate flat
// fields exist only for "backward compatibility" with no current consumer —
// we ignore them.
const getAxisValue = (point: RadarPoint, key: string): number => point.categories?.[key] ?? 0;

const buildRadarRows = (points: RadarPoint[], axes: RadarAxis[]): RadarRow[] =>
  axes.map(axis => {
    const row: RadarRow = { axis: axis.label };
    for (const point of points) {
      row[point.group] = getAxisValue(point, axis.key);
    }
    return row;
  });

export const PerformanceRadarChart: React.FC<PerformanceRadarChartProps> = ({
  data,
  scope,
  height = 280,
  priorLabel = 'Prior Period',
}) => {
  const filtered = useMemo(() => {
    if (!data) return { current: [], prior: [] };
    const keep = (p: RadarPoint) =>
      scope === 'overall' ? p.group === 'overall' : p.group !== 'overall';
    // `prior_period` is optional on the backend type — default to [].
    return {
      current: data.current_period.filter(keep),
      prior: (data.prior_period ?? []).filter(keep),
    };
  }, [data, scope]);

  const hasCurrentData = useMemo(() => hasRadarCurrentData(filtered.current), [filtered]);

  // Axes are the scorecard sections we chart. Derive them from the CURRENT
  // period only — `prior_period` is just the dashed comparison overlay, and for
  // wider windows (60/90d) its data can reach back into older evaluations that
  // carry stray non-section category keys (call-metadata dimensions such as
  // `call-events`, `call-direction`, `context`). Unioning prior keys in turned
  // those into phantom radar spokes; prior values are still read back by these
  // axes below, so extra prior-only keys are simply ignored. See LA-5304.
  const axes = useMemo(() => deriveRadarAxesFromPoints(filtered.current), [filtered]);

  const currentRows = useMemo(() => buildRadarRows(filtered.current, axes), [axes, filtered]);

  const priorByAxis = useMemo(() => {
    const out: Record<string, number> = {};
    const priorRows = buildRadarRows(filtered.prior, axes);
    for (const row of priorRows) {
      // Average prior across groups in scope so we get a single "prior" polygon
      // regardless of program count. This matches the screenshot — one dashed shape.
      const values = Object.entries(row)
        .filter(([k]) => k !== 'axis')
        .map(([, v]) => Number(v))
        .filter(v => Number.isFinite(v));
      out[row.axis] = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    }
    return out;
  }, [axes, filtered]);

  const rows = useMemo(
    () =>
      currentRows.map(row => ({
        ...row,
        __prior__: priorByAxis[row.axis] ?? 0,
      })),
    [currentRows, priorByAxis],
  );

  const colorByGroup = useMemo(
    () =>
      new Map(
        filtered.current.map((point, index) => [
          point.group,
          point.group === 'overall' ? COLOR_PRIMARY : colorForSeriesIndex(index),
        ]),
      ),
    [filtered],
  );

  const config: ChartConfig = useMemo(() => {
    const c: ChartConfig = { __prior__: { label: priorLabel, color: COLOR_MUTED } };
    for (const point of filtered.current) {
      c[point.group] = {
        label: point.group_name,
        color: colorByGroup.get(point.group) ?? COLOR_PRIMARY,
      };
    }
    return c;
  }, [colorByGroup, filtered, priorLabel]);

  if (!hasCurrentData || !rows.length) {
    return <ChartEmptyState height={height} />;
  }

  const axisLabels = axes.map(axis => axis.label).join(', ');
  const ariaLabel =
    scope === 'overall'
      ? `Performance radar chart with axes ${axisLabels}; current period compared to ${priorLabel.toLowerCase()}`
      : `Performance radar chart with axes ${axisLabels}; ${filtered.current.length} program${filtered.current.length === 1 ? '' : 's'} compared to ${priorLabel.toLowerCase()}`;

  return (
    <ChartContainer
      config={config}
      className="!aspect-auto w-full"
      style={{ height }}
      role="img"
      aria-label={ariaLabel}
    >
      <RadarChart data={rows} outerRadius="70%">
        <PolarGrid stroke="hsl(var(--border))" />
        <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }} />
        <PolarRadiusAxis
          domain={[0, 1]}
          tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }}
          angle={30}
        />
        <Tooltip
          contentStyle={{
            background: 'hsl(var(--background))',
            border: '1px solid hsl(var(--border))',
            borderRadius: 6,
            fontSize: 12,
          }}
          // Lift the tooltip above adjacent chart cards; without a z-index the
          // wrapper paints under the sibling chart in the 2-col grid (AQ-476).
          wrapperStyle={{ zIndex: 20 }}
          formatter={(v: unknown) => (typeof v === 'number' ? `${(v * 100).toFixed(1)}%` : '')}
        />

        {/* Legend: always shown when there's data. In Overall scope it reads
            "Current Period" + the prior label; in by-program scope it reads
            each program name + the prior label. Render order below
            determines legend order — current series first, prior last. */}
        {filtered.current.length > 0 && (
          <Legend
            content={<DotsLegend />}
            verticalAlign="bottom"
            wrapperStyle={{ paddingTop: 4 }}
          />
        )}

        {filtered.current.map(point => (
          <Radar
            key={point.group}
            // In Overall scope the single series legend entry should read
            // "Current Period" (paired with the dashed "Prior Period"
            // polygon). In by-program scope we keep the program's own name.
            name={scope === 'overall' ? 'Current Period' : point.group_name}
            dataKey={point.group}
            stroke={colorByGroup.get(point.group) ?? COLOR_PRIMARY}
            fill={colorByGroup.get(point.group) ?? COLOR_PRIMARY}
            fillOpacity={0.25}
            animationDuration={500}
          />
        ))}

        <Radar
          name={priorLabel}
          dataKey="__prior__"
          stroke={COLOR_MUTED}
          strokeDasharray="4 4"
          fill="transparent"
          animationDuration={500}
        />
      </RadarChart>
    </ChartContainer>
  );
};
